import { useState } from 'react';
import { Pencil, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EmailPreviewCard from './EmailPreviewCard';
import InlineEmailEditor from '../email-templates/InlineEmailEditor';
import type { EditableTemplateType } from '../email-templates/templateSections';
import { useGetEmailTemplateQuery, type EmailTemplateType } from '../../store/emailTemplateApi';

interface EditableEmailPreviewCardProps {
  templateType: EditableTemplateType;
  sampleData?: Record<string, string>;
  renderedHtml?: string;
  renderedSubject?: string;
  isRendering?: boolean;
  onTemplateEdited?: () => void;
  /**
   * Per-send override. When provided, the "Edit" button enters per-send mode:
   * saving the editor calls `onOverrideChange(subject, body)` and never touches
   * the template. Pass null to clear the override.
   */
  override?: { subject: string; body: string } | null;
  onOverrideChange?: (override: { subject: string; body: string } | null) => void;
}

function EditableEmailPreviewCard({
  templateType,
  sampleData,
  renderedHtml,
  renderedSubject,
  isRendering,
  onTemplateEdited,
  override,
  onOverrideChange,
}: EditableEmailPreviewCardProps) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();
  const { refetch } = useGetEmailTemplateQuery(templateType as EmailTemplateType);

  const isPerSendMode = !!onOverrideChange;
  const hasOverride = !!override;

  // When an override is set, render the iframe from it instead of the template.
  const effectiveRenderedHtml = hasOverride ? override!.body : renderedHtml;
  const effectiveRenderedSubject = hasOverride ? override!.subject : renderedSubject;

  if (isEditing) {
    return (
      <div className="bg-white rounded-xl border border-purple-200 overflow-hidden">
        <div className="px-4 py-3 bg-purple-50 border-b border-purple-200 flex items-center gap-2">
          <Pencil className="w-4 h-4 text-purple-600" />
          <span className="text-sm font-semibold text-purple-800">
            {isPerSendMode
              ? t('shared.editableEmailPreview.perSendEditHeading')
              : t('shared.editableEmailPreview.editHeading')}
          </span>
        </div>
        <div className="p-4">
          <InlineEmailEditor
            templateType={templateType}
            initialSubject={override?.subject}
            initialBody={override?.body}
            onSaveOverride={isPerSendMode ? (subject, body) => onOverrideChange!({ subject, body }) : undefined}
            onSaved={() => {
              if (isPerSendMode) {
                setIsEditing(false);
              } else {
                refetch().then(() => setIsEditing(false));
                onTemplateEdited?.();
              }
            }}
            onCancel={() => setIsEditing(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end items-center gap-4 mb-2 px-1">
        {isPerSendMode && hasOverride && (
          <button
            type="button"
            onClick={() => onOverrideChange!(null)}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            {t('shared.editableEmailPreview.resetOverride')}
          </button>
        )}
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className="flex items-center gap-1.5 text-purple-600 hover:text-purple-800 font-medium text-sm"
        >
          <Pencil className="w-3.5 h-3.5" />
          {isPerSendMode
            ? (hasOverride
                ? t('shared.editableEmailPreview.editOverride')
                : t('shared.editableEmailPreview.customizeForThisSend'))
            : t('shared.editableEmailPreview.editTemplate')}
        </button>
        {!isPerSendMode && (
          <button
            type="button"
            onClick={() => navigate('/settings/email-templates')}
            className="flex items-center gap-1.5 text-gray-400 hover:text-gray-600 text-xs"
          >
            <Settings className="w-3.5 h-3.5" />
            {t('shared.editableEmailPreview.customize')}
          </button>
        )}
      </div>
      <EmailPreviewCard
        templateType={templateType}
        sampleData={sampleData}
        renderedHtml={effectiveRenderedHtml}
        renderedSubject={effectiveRenderedSubject}
        isRendering={isRendering}
      />
    </div>
  );
}

export default EditableEmailPreviewCard;
